import { Kanban, ShieldCheck, Factory, ChevronRight } from 'lucide-react';
import type { AdminOrder } from '../../../pages/admin/adminOrdersShared';
import { statusMeta, nextStatuses } from '../../../pages/admin/adminOrdersShared';
import { SALES_STAGE_META } from './crmLabels';
import { CRM_ALERT_ORANGE, nextStepBadgesForOrder } from './crmDealStrip';
import type { CrmPipelineCard, SalesStage } from '../../../api';

type Props = {
  order: AdminOrder;
  updating: boolean;
  onOpenDeal: () => void;
  onQuickAction?: () => void;
  quickActionLabel?: string;
  hapticSelection: () => void;
};

function orderAsPipelineCard(order: AdminOrder): CrmPipelineCard {
  return {
    id: order.id,
    order_number: order.order_number,
    status: order.status,
    sales_stage: (order.sales_stage || 'lead_received') as SalesStage,
    final_amount: order.final_amount,
    assigned_manager_id: order.assigned_manager_id ?? null,
    has_open_task: Boolean(order.has_valid_open_task) || Boolean(order.has_overdue_task),
    has_valid_open_task: Boolean(order.has_valid_open_task),
    has_overdue_task: Boolean(order.has_overdue_task),
    order_has_pricing_drift: Boolean(order.order_has_pricing_drift),
    days_in_stage: 0,
    user: {
      id: order.user.id,
      telegram_id: order.user.telegram_id,
      first_name: order.user.first_name,
      username: order.user.username,
      role: order.user.role,
    },
  };
}

function resolveQuickAction(order: AdminOrder): { label: string; show: boolean } {
  if (!order.assigned_manager_id) {
    return { label: 'Взять в работу', show: true };
  }
  const next = nextStatuses(order.status)[0];
  if (next === 'in_production') return { label: 'В цех', show: true };
  if (next === 'confirmed') return { label: 'Подтвердить', show: true };
  if (next === 'ready') return { label: 'Готов', show: true };
  if (next) {
    const m = statusMeta(next);
    return { label: m.label, show: true };
  }
  return { label: '', show: false };
}

export function CrmOrderListStrip({
  order,
  updating,
  onOpenDeal,
  onQuickAction,
  quickActionLabel,
  hapticSelection,
}: Props) {
  const logistics = statusMeta(order.status);
  const stageKey = (order.sales_stage || 'lead_received') as SalesStage;
  const stageMeta = SALES_STAGE_META[stageKey];
  const badges = nextStepBadgesForOrder(orderAsPipelineCard(order));
  const quick = quickActionLabel
    ? { label: quickActionLabel, show: Boolean(onQuickAction) }
    : resolveQuickAction(order);
  const isPending = order.status === 'pending';
  const pricingDrift = Boolean(order.order_has_pricing_drift);
  const client =
    order.user.first_name || order.user.username || `TG ${order.user.telegram_id}`;

  return (
    <div
      className="glass rounded-2xl overflow-hidden relative"
      style={
        isPending
          ? { boxShadow: '0 0 0 2px rgba(252,129,74,0.45), 0 8px 24px rgba(252,129,74,0.08)' }
          : undefined
      }
    >
      {pricingDrift && (
        <span
          className="absolute top-2.5 right-2.5 w-2.5 h-2.5 rounded-full z-10 animate-pulse"
          style={{ background: CRM_ALERT_ORANGE, boxShadow: '0 0 8px color-mix(in srgb, var(--orange, #FC814A) 80%, transparent)' }}
          title="Изменились цены — откройте карточку"
        />
      )}
      <button
        type="button"
        onClick={() => {
          hapticSelection();
          onOpenDeal();
        }}
        className="press w-full px-3 py-2.5 flex items-center gap-2.5 text-left touch-manipulation min-h-[52px]"
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: stageMeta.bg, border: `1px solid ${stageMeta.border}` }}
        >
          <span className="text-[10px] font-black" style={{ color: stageMeta.color }}>
            {stageMeta.label.slice(0, 2)}
          </span>
        </div>
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-sm truncate" style={{ color: 'var(--text-1)' }}>
              {order.order_number}
            </span>
            <span className="font-bold text-sm shrink-0" style={{ color: 'var(--cyan-bright)' }}>
              {order.final_amount.toLocaleString('ru-RU')} ₽
            </span>
          </div>
          <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--text-3)' }}>
            {client}
            {order.user.role === 'partner' ? ' · Партнёр' : ''}
          </p>
          <p className="text-[10px] truncate mt-0.5">
            <span style={{ color: stageMeta.color }}>{stageMeta.label}</span>
            <span style={{ color: 'var(--text-3)' }}> · {logistics.label}</span>
          </p>
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {badges.map((b) => (
                <span
                  key={b.label}
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    background: 'color-mix(in srgb, var(--orange, #FC814A) 20%, transparent)',
                    color: b.color,
                  }}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronRight size={16} className="shrink-0" style={{ color: 'var(--text-3)' }} />
      </button>

      <div className="flex gap-2 px-3 pb-3 pt-0" onClick={(e) => e.stopPropagation()}>
        {quick.show && onQuickAction && (
          <button
            type="button"
            disabled={updating}
            onClick={() => {
              hapticSelection();
              onQuickAction();
            }}
            className="press flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-bold touch-manipulation min-h-[44px] disabled:opacity-50"
            style={{
              background: !order.assigned_manager_id
                ? 'rgba(246,173,85,0.18)'
                : order.status === 'confirmed'
                  ? 'rgba(159,122,234,0.18)'
                  : 'rgba(72,187,120,0.15)',
              color: !order.assigned_manager_id ? '#F6AD55' : '#68D391',
              border: '1px solid var(--border-subtle)',
            }}
          >
            {!order.assigned_manager_id ? (
              <ShieldCheck size={13} />
            ) : order.status === 'confirmed' ? (
              <Factory size={13} />
            ) : null}
            {updating ? '…' : quick.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            hapticSelection();
            onOpenDeal();
          }}
          className="press flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-bold touch-manipulation min-h-[44px]"
          style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
        >
          <Kanban size={13} /> Карточка сделки
        </button>
      </div>
    </div>
  );
}
